/**
 * Runtime brand colours for the SVG layer (Plan C fase 3.2).
 *
 * THE SECOND LAYER, AND WHY IT IS NEEDED AT ALL
 * ---------------------------------------------
 * `tailwind-preset.js` makes every `bg-accent` / `ring-accent/30`
 * class indirect: the class compiles to `rgb(var(--bi-…) / <alpha>)` and a
 * tenant theme overrides the variable. That covers the whole HTML chrome.
 *
 * It does NOT cover the charts. A chart mark is an SVG `fill` / `stroke`
 * set as a PRESENTATION ATTRIBUTE (`<rect fill={color} />`), and a
 * presentation attribute is not a CSS declaration — `fill="var(--x)"` is
 * simply invalid and paints nothing. The colour has to be a concrete
 * string by the time React writes the attribute. So this module reads the
 * same custom properties the preset defines and hands the charts a real
 * `rgb(r g b)` value.
 *
 * WHEN IT IS READ
 * ---------------
 * Once, at module load. The tenant stylesheet is a `<link>` in
 * `index.html`, so it is parsed and applied before any module of the
 * bundle executes: by the time this file runs, `--bi-accent` already
 * holds the tenant's value. There is no subscription and no re-read,
 * because a theme cannot change inside a page's lifetime — changing it in
 * the console produces a new stylesheet on the next load.
 *
 * ABOUT THE `brand.js` VALUE USED WHEN THE VARIABLE IS ABSENT
 * -----------------------------------------------------------
 * This is not a fallback that invents a number. `brandCssVars()` builds
 * the `:root` block FROM `brand.js`, so an unthemed page's variable holds
 * exactly `brand[role][step]` — the two are the same constant one
 * function apart, and `theme-tokens.test` pins that. The variable being
 * absent means the preset's stylesheet is not loaded at all, which is the
 * documented breakage where every `*-primary-*` class already renders
 * transparent; the page is visibly broken with or without this line, and
 * `reportMissingBrandVars()` says so once in the console rather than
 * turning a styling failure into a blank screen.
 */
import { brand, brandVarName } from '../brand.js';

let warned = false;

function reportMissingBrandVars(varName) {
  if (warned) return;
  // Only complain where the complaint is true. A jsdom unit test renders
  // a chart with NO stylesheet at all — that is not a misconfigured
  // consumer, and a warning there would be noise that trains people to
  // ignore the real one. With at least one stylesheet attached and the
  // variable still missing, the preset genuinely is not loaded.
  if (typeof document !== 'undefined' && document.styleSheets.length === 0) return;
  warned = true;
  // eslint-disable-next-line no-console
  console.error(
    `[bi-components] CSS custom property ${varName} is not defined on ` +
      ':root. The @pervasivemind/bi-components Tailwind preset is not ' +
      'loaded, so every *-primary-* / *-accent-* class in this page is ' +
      'also rendering transparent. Fix the consumer tailwind.config.js ' +
      '(presets: [omnitokPreset, biComponentsPreset]); charts are using ' +
      'the compiled defaults from brand.js meanwhile.',
  );
}

/** Raw channel triplet of a `--bi-*` property, or `null` when unset. */
export function readBrandChannels(varName) {
  if (typeof document === 'undefined' || !document.documentElement) return null;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return raw === '' ? null : raw;
}

/**
 * A concrete colour string for an SVG attribute, from the live variable.
 *
 * @param {string} role  `'primary'` | `'accent'`
 * @param {string|number} [step]  omitted for DEFAULT; `600` is accent.dark
 */
export function runtimeBrandColor(role, step) {
  const varName = brandVarName(role, step);
  const channels = readBrandChannels(varName);
  if (channels === null) {
    reportMissingBrandVars(varName);
    // The step names the variable; the brand.js key is the kit's name.
    return brand[role][step === undefined ? 'DEFAULT' : step === 600 ? 'dark' : step];
  }
  return `rgb(${channels})`;
}

/**
 * The chart series palette, with the accent slot (S2, DQ3) bound to the
 * tenant's brand colour.
 *
 * Only that slot moves. The others are fixed on purpose: they are a
 * validated categorical set, and the MVP lets a tenant pick ONE brand
 * colour precisely so nobody can turn an eight-series chart into eight
 * shades of one hue. The `neutral` chart chrome and the `semantic` /
 * `band*` tones are not here at all; they are not themeable (fase 3.4).
 */
export function runtimeChartPalette() {
  return [brand.palette[0], runtimeBrandColor('accent'), ...brand.palette.slice(2)];
}

/**
 * `rgb(var(--bi-accent))` — for a CSS context, not an SVG attribute.
 *
 * Use this in `style={{ … }}` and in `linear-gradient(...)` strings: a
 * CSS declaration resolves `var()` natively, so the value stays indirect
 * all the way to paint and nothing has to be read back with
 * `getComputedStyle`. `runtimeBrandColor` above is only for the SVG
 * presentation attributes that cannot hold a `var()` at all.
 */
export function cssBrandColor(role, step) {
  return `rgb(var(${brandVarName(role, step)}))`;
}

export const THEME_VAR_PREFIX = '--bi-';
